import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The institutional site's root error boundary (W8-DRIVER-SHIFT-TRUTH, D442; finding X-08 — "no app has an ErrorBoundary").
 * Every other web app renders `@vello/web-theme`'s `AppErrorBoundary`; the site is still on its own theme (it stays in
 * `PENDING_WEB_APPS` by the owner's scope decision of 2026-09-23 — its move onto the shared theme is a separate, signed-off
 * change), so it carries this small local boundary with the site's own classes. A crashed page shows a sentence and a
 * reload instead of a blank page; the report (a bounded message, e-mail addresses and long digit runs masked) goes to the
 * browser console. The copy follows the page language (`<html lang>`), then the browser's, then Portuguese.
 */
const COPY: Readonly<Record<string, { title: string; body: string; retry: string; home: string }>> = {
  pt: { title: 'Algo deu errado ao mostrar esta página.', body: 'Tente recarregar. Se continuar assim, volte ao início e tente de novo em instantes.', retry: 'Recarregar', home: 'Voltar ao início' },
  en: { title: 'Something went wrong showing this page.', body: 'Try reloading. If it keeps happening, go back to the home page and try again in a moment.', retry: 'Reload', home: 'Back to home' },
  es: { title: 'Algo salió mal al mostrar esta página.', body: 'Prueba a recargar. Si sigue igual, vuelve al inicio e inténtalo de nuevo en un momento.', retry: 'Recargar', home: 'Volver al inicio' },
};

/** The default language of the site when the page names none (`lang` empty or `und`, as when the runtime config never loaded). */
const DEFAULT_LANGUAGE = 'pt';

/**
 * The language of the crash screen: the page's `<html lang>` when it names one the screen has, else the browser's own
 * language, else Portuguese (D854, L7b-022: with `site-config.json` missing the document language was `und` and the
 * screen fell to English in front of a Brazilian visitor).
 */
export function crashLanguage(htmlLang: string | undefined, browserLang: string | undefined): string {
  for (const tag of [htmlLang, browserLang]) {
    const primary = (tag ?? '').toLowerCase().split('-')[0] ?? '';
    if (primary !== '' && primary !== 'und' && COPY[primary] !== undefined) return primary;
  }
  return DEFAULT_LANGUAGE;
}

function redact(text: string): string {
  return text
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '<email>')
    .replace(/\+?\d[\d\s().-]{6,}\d/g, (run) => (run.replace(/\D/g, '').length >= 8 ? '<digits>' : run))
    .slice(0, 300);
}

export class SiteErrorBoundary extends Component<{ readonly children?: ReactNode }, { crashed: boolean }> {
  override state = { crashed: false };

  static getDerivedStateFromError(): { crashed: boolean } {
    return { crashed: true };
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo): void {
    console.error(`[vello:crash] render site: ${redact(error instanceof Error ? error.message : String(error))}`);
  }

  override render(): ReactNode {
    if (!this.state.crashed) return this.props.children;
    const copy = COPY[crashLanguage(document.documentElement.lang, typeof navigator !== 'undefined' ? navigator.language : undefined)] ?? COPY.pt!;
    return (
      <main role="alert" data-testid="site-crash-fallback" style={{ maxWidth: 480, margin: '0 auto', padding: 'var(--space-10) var(--space-6)', textAlign: 'center', display: 'grid', gap: 'var(--space-4)' }}>
        <p style={{ margin: 0, color: 'var(--color-text-primary)', fontSize: 'var(--text-title-size)' }}>{copy.title}</p>
        <p className="notice-body">{copy.body}</p>
        <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
          {copy.retry}
        </button>
        <a className="btn btn--ghost" href="/" data-testid="site-crash-home">
          {copy.home}
        </a>
      </main>
    );
  }
}
